/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the patient summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';


import { makeStyles, Theme } from '@material-ui/core/styles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

/**
 * Used to format text.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */

/**
 * Used to style expanding areas.
 */





/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { faChevronUp } from '@fortawesome/pro-duotone-svg-icons/faChevronUp';
import Collapse from '@material-ui/core/Collapse';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { IEvent, IEventType, IFormType, IPatient } from '@ngt/online-patient-management-ui';
import PatientSummaryForms from './PatientSummaryForms';
import { Patient } from '../../api/Neon.Dtos';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientSummaryEventProps {
    patient: IPatient;
    eventType: IEventType;
    formTypes: IFormType[];
    event: IEvent | null;
    eventRepeat: number;
    loadingEvents: boolean;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        paddingLeft: theme.spacing(6)
    },
    item: {
        marginTop: -1,
        border: `1px solid ${theme.palette.grey[300]}`
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


const PatientSummaryEvent: React.FunctionComponent<IPatientSummaryEventProps> = ({
    patient,
    formTypes,
    eventType,
    event,
    eventRepeat,
    loadingEvents
}) => {
    if (!patient) {
        throw new Error('Patient must be defined.');
    }

    if (!eventType) {
        throw new Error('eventType must be defined.');
    }

    if (!formTypes) {
        throw new Error('formTypes must be defined.');
    }

    if (!eventRepeat) {
        throw new Error('eventRepeat must be defined.');
    }

    const classes = useStyles();

    const [expand, setExpand] = React.useState(true);

    const onClick = React.useCallback(() => {
        setExpand(!expand);
    }, [setExpand, expand])

    const eventName = eventType.code === 'pre-registration' && ((patient as Patient).treatmentId !== 1 && (patient as Patient).treatmentId !== 2) ? 'Screening' : (event ? event.name : eventType.repeating ? `${eventType.name} (${eventRepeat})` : eventType.name);

    return (
        <>
            <ListItem
                button
                className={classes.item}
                onClick={onClick}
            >
                <ListItemIcon>
                    <FontAwesomeIcon fixedWidth icon={expand ? faChevronUp : faChevronDown} />
                </ListItemIcon>
                <ListItemText primary={<strong>{eventName}</strong>} />
            </ListItem>
            <Collapse
                in={expand}
            >
                <div
                    className={classes.container}
                >
                    {
                        formTypes.map(formType => {

                            return (
                                <PatientSummaryForms
                                    key={formType.id}
                                    event={event}
                                    eventRepeat={eventRepeat}
                                    eventType={eventType}
                                    formType={formType}
                                    loadingEvents={loadingEvents}
                                    patient={patient}
                                />
                            );
                        })
                    }
                </div>
            </Collapse>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default PatientSummaryEvent;
